import React, { useState, useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TextField, { OutlinedTextFieldProps } from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import styled from 'styled-components';
import Popup from 'semantic-ui-react/dist/commonjs/modules/Popup';
import TooltipModal from '../../components/mobileTooltipModal/tooltipModal';
import MaskedInput from 'react-text-mask';
import COLORS from '../../common/styles/color-pallete';
import { FONT_FAMILY } from '../../common/styles/fontStyles';
import { CORRECT_ICON, EDIT_ICON, INFO_GREY_ICON } from '@Images';
import { CNY_WALLET_ID_NOTE, CNY_RECIPIENT_TYPE } from '../../utils/constants';

const Container = styled.div<{
  $top: boolean;
  $error?: boolean;
  $focused?: boolean;
  $bottomMargin?: number;
}>`
  padding-bottom: ${(p) => (p.$top ? '20px' : '0px')};
  margin-bottom: ${(p) => (p.$bottomMargin ? `${p.$bottomMargin}px !important` : '0px')};
  .label-color {
    color: ${(p) =>
      p.$error ? 'red' : p.$focused ? `${COLORS.textGrey}` : `${COLORS.textLightGrey}`} !important;
  }
  div:focus-within {
    .label-color {
      margin-top: 0px;
    }
  }
`;
const NoteText = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: left;
  margin-top: 2%;
  width: 100%;
`;
const InfoTextContainer = styled.span<{ isError?: boolean | undefined | string }>`
  color: ${COLORS.textGrey};
  font-size: 12px;
  margin-top: ${({ isError = false }) => (isError ? 16 : 8)}px;
  display: flex;
  width: 325px;
`;
interface IMultilineTextFields extends OutlinedTextFieldProps {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  multiLine?: boolean;
  row?: number;
  defaultValue: string;
  placeholder: string;
  destination_currency?: string;
  input: any;
  name: string;
  disabled: boolean;
  inputTypeField?: string;
  cardImage?: React.ReactElement;
  type: string;
  tollTipSize?: {
    height: string;
    width: string;
  };
  meta: {
    error: string | boolean;
    touched: boolean;
    active: boolean;
  };
  boxSize: string;
  showhelperText?: boolean;
  helperTextNote?: string;
  tooltip?: any;
  tooltipTitle?: string;
  fieldClass?: any;
  maxWidth?: number;
  title?: string;
  content?: string;
  mobiletooltip?: string;
  toolTipClassName?: string;
  onClickInputAdornment?: () => void;
  customMargin?: string;
  showSuccessIcon?: boolean;
  showEditIcon?: boolean;
  onEditBtnClick?: () => void;
  shrink?: boolean;
  successIcon?: string;
  errorIcon?: string;
  note?: string;
  informationText?: string;
}

export const useStyles = makeStyles((theme: any): any => {
  return {
    '@global': {
      '.MuiInputLabel-outlined ': {
        fontFamily: 'hellix-regular',
        fontSize: '14px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        boxSizing: 'content-box',
        color: COLORS.textLightGrey,
        marginTop: '-3px'
      },
      '.MuiFormHelperText-root': {
        fontSize: '0.68rem',
        marginTop: '0px'
      },
      '.MuiFormLabel-root.Mui-focused': {
        color: COLORS.textLightGrey
      },
      '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #000'
      },
      '.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
        border: '1px solid red !important'
      },
      '.error-helper-text': {
        marginLeft: '5px !important'
      }
    },
    formControl: {
      margin: (p: any) => (p.maxWidth ? 0 : p.customMargin ? p.customMargin : theme.spacing(1)),
      fullWidth: true,
      display: 'flex',
      wrap: 'nowrap',
      maxWidth: (p: any) => (p.maxWidth ? p.maxWidth : '325px'),
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%'
      },
      [theme.breakpoints.down('sm')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      [theme.breakpoints.down('md')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      height: '50px',
      color: '#000000'
    },
    formControlSmall: {
      margin: (p: any) => (p.customMargin ? p.customMargin : theme.spacing(1)),
      fullWidth: true,
      display: 'flex',
      wrap: 'nowrap',
      maxWidth: '225px',
      height: '50px',
      [theme.breakpoints.down('sm')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      [theme.breakpoints.down('md')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      color: '#000000'
    },
    input: {
      WebkitTextFillColor: '#333',
      height: '50px',
      fontFamily: 'hellix-regular',
      color: '#333',
      fontSize: '18px',
      caretColor: COLORS.brandPrimary,
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    },
    inputDisabled: {
      WebkitTextFillColor: '#a4a5b1',
      height: '50px',
      [theme.breakpoints.down('sm')]: {
        height: (p: any) => (p.meta.error && p.touched ? 'auto' : '50px')
      },
      [theme.breakpoints.down('md')]: {
        height: (p: any) => (p.meta.error && p.touched ? 'auto' : '50px')
      },
      color: '#000000',
      fontSize: '16px',
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    }
  };
});

const useStylesNotes = makeStyles((theme: any): any => {
  return {
    '@global': {
      '.MuiInputLabel-outlined ': {
        fontFamily: 'hellix-regular',
        fontSize: '14px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        boxSizing: 'content-box',
        color: '#333'
      },
      '.MuiFormHelperText-root': {
        fontSize: '0.68rem'
      },
      '.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#000'
      },
      '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #000 !important'
      }
    },
    formControl: {
      margin: theme.spacing(1),
      fullWidth: true,
      display: 'flex',
      wrap: 'nowrap',
      maxWidth: (p: any) => (p.maxWidth ? p.maxWidth : '325px'),
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%'
      },
      [theme.breakpoints.down('sm')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      [theme.breakpoints.down('md')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      height: '50px',
      color: '#000000'
    },
    formControlSmall: {
      margin: theme.spacing(1),
      fullWidth: true,
      display: 'flex',
      wrap: 'nowrap',
      maxWidth: '225px',
      height: '50px',
      [theme.breakpoints.down('sm')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      [theme.breakpoints.down('md')]: {
        height: (p: any) => (p.meta.error && p.meta.touched ? 'auto' : '50px')
      },
      color: '#000000'
    },
    input: {
      // WebkitTextFillColor:"#96a4b9" ,

      height: (p: any) => (p.multiline ? 'auto' : ''),
      color: '#000000',
      fontSize: '18px',
      caretColor: COLORS.brandPrimary,
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    },
    inputDisabled: {
      WebkitTextFillColor: '##8c8d9a',
      height: (p: any) => (p.multiline ? 'auto' : ''),
      [theme.breakpoints.down('sm')]: {
        height: (p: any) => (p.meta.error && p.touched ? 'auto' : '50px')
      },
      [theme.breakpoints.down('md')]: {
        height: (p: any) => (p.meta.error && p.touched ? 'auto' : '50px')
      },
      color: '#000000',
      fontSize: '16px',
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    }
  };
});

const PopUpStyle = styled.div`
  font-family: hellix-regular !important;
  font-size: 12px !important;
  font-weight: normal !important;
  color: ${COLORS.textLightGrey} !important;
  letter-spacing: 1 !important;
  line-height: 1.7 !important;
`;

const HelperTextBox = styled.div`
  position: absolute;
  left: 0px;
  z-index: 19;
  box-shadow: rgba(0, 0, 0, 0.1) 1px 1px 3px 0px;
  border: 0.5px solid rgb(237, 237, 237);
  background-color: rgb(255, 255, 255);
  font-family: ${FONT_FAMILY.hellix.regular};
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: rgb(116, 124, 137);
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;
`;

function TextMaskCustom(props: any) {
  const { inputRef, ...other } = props;
  const checkAmericanExpress = ['37', '34'].includes(
    props && props.value.split('').splice(0, 2).join('')
  );
  const otherCards = [
    /[1-9]/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/
  ];
  const americanExpress = [
    /[1-9]/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ' ',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/
  ];
  return (
    <MaskedInput
      {...other}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={
        props && props.name === 'expiry_date'
          ? [/[0-9]/, /\d/, '/', /\d/, /\d/]
          : checkAmericanExpress
          ? americanExpress
          : otherCards
      }
      guide={false}
      placeholderChar={'\u2000'}
      // showMask
    />
  );
}

export const OutlinedTextFields: React.FC<IMultilineTextFields> = (props) => {
  const [dimentions, setDimentions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener('resize', updateWindowDimensions);
    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  const updateWindowDimensions = () => {
    setDimentions({ width: window.innerWidth, height: window.innerHeight });
  };
  const classes = useStyles(props);
  const addressTooltipTitle =
    dimentions.width < 1024 && props.mobiletooltip ? 'Unit/Apartment/Floor Number' : null;
  //@ts-ignore
  let inputClass = props.boxSize === 'medium' ? classes.formControlSmall : classes.formControl;
  if (props.fieldClass)
    inputClass = inputClass.concat(props.fieldClass ? ` ${props.fieldClass}` : '');
  const chooseModal = dimentions.width > 1080 || props.input.name === 'cvc' ? true : false;

  // Determine if bottom margin should be added
  const noteCount = [
    props?.input?.name === 'beneficiary_identification_value' &&
      props?.destination_currency === 'CNY',
    props?.note &&
      props?.input?.name === 'beneficiary_account_number' &&
      props?.destination_currency === 'CNY',
    props?.note
  ].filter(Boolean).length;

  const bottomMargin = noteCount * 50;

  return (
    <Container
      id={`${props.input.name}_container`} //for testing automation
      $top={props.meta.active && props.showhelperText && !props.meta.error ? true : false}
      $error={props.meta.touched && props.meta.error ? true : false}
      $focused={props.meta.active}
      $bottomMargin={bottomMargin}
    >
      <FormControl
        variant="outlined"
        className={inputClass}
        error={props.meta.touched && props.meta.error ? true : false}
      >
        <TextField
          name={props.input.name}
          type={props.type}
          id={props.placeholder}
          label={props.label || props.placeholder}
          InputLabelProps={{ className: 'label-color', shrink: props.shrink }}
          disabled={props.disabled}
          placeholder={props.placeholder}
          {...props.input}
          multiline={props.multiLine}
          onKeyUp={props.onKeyUp}
          InputProps={
            props.showSuccessIcon
              ? {
                  //@ts-ignore
                  className: classes.input,
                  endAdornment: (
                    <InputAdornment position="end">
                      <img width={'20px'} src={CORRECT_ICON} alt="success" />
                    </InputAdornment>
                  )
                }
              : props.showEditIcon
              ? {
                  //@ts-ignore
                  className: classes.inputDisabled,
                  endAdornment: (
                    <InputAdornment position="end">
                      <img
                        src={EDIT_ICON}
                        width="25px"
                        alt="edit"
                        onClick={() => props.onEditBtnClick && props.onEditBtnClick()}
                      />
                    </InputAdornment>
                  )
                }
              : (props.inputTypeField === 'debit card' && props.input.name === 'card_number') ||
                props.input.name === 'expiry_date'
              ? {
                  //@ts-ignore
                  className: props.disabled ? classes.inputDisabled : classes.input,
                  inputComponent: TextMaskCustom,
                  endAdornment:
                    props.inputTypeField === 'debit card' && props.input.name === 'card_number' ? (
                      <InputAdornment position="end">{props.cardImage}</InputAdornment>
                    ) : null
                }
              : props.input.name === 'routing_code_value_1'
              ? {
                  //@ts-ignore
                  className: props.disabled ? classes.inputDisabled : classes.input,
                  endAdornment: (
                    <InputAdornment position="end" onClick={props.onClickInputAdornment}>
                      {props.cardImage}
                    </InputAdornment>
                  )
                }
              : props.input.name === 'panCardNumber' || props.input.name === 'panName'
              ? {
                  //@ts-ignore
                  className: props.disabled ? classes.inputDisabled : classes.input,
                  endAdornment: (
                    <InputAdornment position="end">
                      {(props.meta.error || props.successIcon) && props.input.value ? (
                        <img
                          src={props.meta.error ? props.errorIcon : props.successIcon}
                          alt="pan-info-icon"
                        />
                      ) : null}
                    </InputAdornment>
                  )
                }
              : {
                  //@ts-ignore
                  className: props.disabled ? classes.inputDisabled : classes.input,
                  endAdornment: props.tooltip && (
                    <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                      {chooseModal ? (
                        <Popup
                          trigger={
                            <img
                              id={`${props.input.name}-tooltip`}
                              alt="tooltip"
                              className={props.toolTipClassName || ''}
                              src={INFO_GREY_ICON}
                              width={(props.tollTipSize && props.tollTipSize.width) || '20px'}
                              height={(props.tollTipSize && props.tollTipSize.height) || '20px'}
                            />
                          }
                          content={
                            <PopUpStyle id={`${props.input.name}-content`}>
                              {props.tooltip}
                            </PopUpStyle>
                          }
                          size="tiny"
                          position="right center"
                          hoverable
                        />
                      ) : (
                        <TooltipModal
                          triggerContent={
                            <img
                              src={INFO_GREY_ICON}
                              className={props.toolTipClassName || ''}
                              alt="info"
                              width="20px"
                              height="20px"
                            />
                          }
                          title={addressTooltipTitle || props.tooltipTitle || props.input.name}
                          content={props.tooltip}
                        />
                      )}
                    </InputAdornment>
                  )
                }
          }
          rows={props.row}
          defaultValue={props.defaultValue}
          variant={'outlined'}
          fullWidth={true}
          error={props.meta.touched && props.meta.error ? true : false}
          helperText={
            props.meta.active ? (
              <HelperTextBox id={`${props.input.name}_helperText`}>
                {props.helperTextNote}
              </HelperTextBox>
            ) : (
              ''
            )
          }
        />
        {props?.input?.name === 'beneficiary_identification_value' &&
          props?.destination_currency === 'CNY' && (
            <NoteText className="fieldNotes">{CNY_RECIPIENT_TYPE}</NoteText>
          )}
        {props?.note &&
          props?.input?.name === 'beneficiary_account_number' &&
          props?.destination_currency === 'CNY' && (
            <NoteText className="fieldNotes">{CNY_WALLET_ID_NOTE}</NoteText>
          )}
        {props?.note && <NoteText className="fieldNotes">{props.note}</NoteText>}
        {props.meta.touched && props.meta.error && (
          <FormHelperText id="error-msg" className="error-helper-text">
            {props.meta.error}
          </FormHelperText>
        )}
      </FormControl>
      {!props.meta.error &&
      props.meta.touched &&
      props.informationText &&
      props.informationText.length > 0 ? (
        <InfoTextContainer isError={props.meta.touched && props.meta.error}>
          {props.informationText}
        </InfoTextContainer>
      ) : null}
    </Container>
  );
};

export const OutlinedNotesFields: React.FC<IMultilineTextFields> = (props) => {
  const classes = useStylesNotes(props);

  // Determine if bottom margin should be added
  const noteCount = [
    props?.input?.name === 'beneficiary_identification_value' &&
      props?.destination_currency === 'CNY',
    props?.note &&
      props?.input?.name === 'beneficiary_account_number' &&
      props?.destination_currency === 'CNY',
    props?.note
  ].filter(Boolean).length;

  const bottomMargin = noteCount * 50;

  return (
    <Container
      $top={props.meta.active && props.showhelperText && !props.meta.error ? true : false}
      $bottomMargin={bottomMargin}
    >
      <FormControl
        variant="outlined"
        //@ts-ignore
        className={props.boxSize === 'medium' ? classes.formControlSmall : classes.formControl}
        error={props.meta.touched && props.meta.error ? true : false}
      >
        <TextField
          name={props.input.name}
          type={props.type}
          id="outlined-multiline-static"
          label={props.label || props.placeholder}
          disabled={props.disabled}
          {...props.input}
          multiline={props.multiLine}
          InputLabelProps={{
            shrink: true
          }}
          InputProps={{
            placeholder: props.placeholder,

            //@ts-ignore
            className: props.disabled ? classes.inputDisabled : classes.input,
            endAdornment: props.tooltip && props.tooltip !== false && (
              <InputAdornment position="end">
                <Popup
                  trigger={
                    <img
                      src={INFO_GREY_ICON}
                      alt="info"
                      className={props.toolTipClassName || ''}
                      width={(props.tollTipSize && props.tollTipSize.width) || '20px'}
                      height={(props.tollTipSize && props.tollTipSize.height) || '20px'}
                    />
                  }
                  size="tiny"
                  position="top right"
                >
                  {props.tooltip}
                </Popup>
              </InputAdornment>
            )
          }}
          rows={props.row}
          defaultValue={props.defaultValue}
          variant={'outlined'}
          fullWidth={true}
          error={props.meta.touched && props.meta.error ? true : false}
          helperText={
            props.meta.active && props.showhelperText && !props.meta.error ? (
              <HelperTextBox>{props.helperTextNote}</HelperTextBox>
            ) : (
              ''
            )
          }
        />
        {props.meta.touched && props.meta.error && (
          <FormHelperText>{props.meta.error}</FormHelperText>
        )}
      </FormControl>
    </Container>
  );
};
